import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { SwipableBox } from '@eneco/custom-components/SwipableBox/SwipableBox';
import { Bleed, TabNavigation as SparkyTabNavigation, PageGrid } from '@eneco/sparky';

import type { TabNavigationContent } from './tab-navigation-content.types';

/**
 * `TabNavigation` block for the CMS-agnostic lane, serving **both** providers. Mirrors the legacy
 * component (`libs/sitecore/components/content/src/TabNavigation/TabNavigation.tsx`): a swipeable Sparky
 * tab bar of links, bleeding to the viewport edge below `lg`, inside a full-width `PageGrid` item.
 *
 * Known simplification: the legacy component wraps each tab in `@eneco/link`'s `useLinkComponent`
 * (Sitecore `editable` markup, client-side routing) around a link-less `TabNavigation.Item`. Here the
 * tab takes `href`/`target` itself and renders Sparky's own anchor — valid markup instead of a `<button>`
 * inside an `<a>`, but navigation is a plain page load and there is no inline editing.
 *
 * See `tab-navigation-content.types.ts` for the content shape and the Sitecore-lane fallback.
 */
const TabNavigation: FC = () => {
  const content = useContent<TabNavigationContent>();
  const tabs = content.siteWideContent?.items
    ? content.siteWideContent.items.map((item, index) => ({ key: item._key ?? String(index), link: linkWithUrl(item.link) }))
    : (content.items ?? []).map((item, index) => ({ key: item.id ?? String(index), link: linkWithUrl(item.link) }));
  const ariaLabel = content.ariaLabel ?? content.siteWideContent?.title;

  return (
    <PageGrid>
      <PageGrid.Item gridColumn="1/-1">
        <Bleed horizontal={{ initial: 'gridGutter', lg: '0' }}>
          <SwipableBox>
            <SparkyTabNavigation ariaLabel={ariaLabel}>
              {tabs.map(tab =>
                tab.link ? (
                  <SparkyTabNavigation.Item key={tab.key} href={tab.link.href} target={tab.link.target}>
                    {tab.link.text}
                  </SparkyTabNavigation.Item>
                ) : null,
              )}
            </SparkyTabNavigation>
          </SwipableBox>
        </Bleed>
      </PageGrid.Item>
    </PageGrid>
  );
};

export default TabNavigation;
